import type { RequestCategory } from "@nlc/shared-types";

import { SearchIcon } from "lucide-react";

import type {
  CategoryWithIcon,
  DesignType,
  SubcategoryWithIcon,
} from "../types";

import { designTypeMatchesQuery, normalizeSearchQuery } from "../utils";
import { DesignTypesCarousel } from "./DesignTypesCarousel";
import { SearchButton } from "./SearchButton";

const CATEGORY_TAG_LABELS: Record<RequestCategory, string> = {
  graphic: "Graphics",
  video: "Video",
  webui: "Web & UI",
};

type MultiCategoryResultsProps = {
  categories: CategoryWithIcon[];
  designTypes: DesignType[];
  onSearchChange: (value: string) => void;
  searchQuery: string;
  subcategories: SubcategoryWithIcon[];
};

export function MultiCategoryResults({
  categories,
  designTypes,
  onSearchChange,
  searchQuery,
  subcategories,
}: MultiCategoryResultsProps) {
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onSearchChange(e.target.value);
  };

  const categoryIds = new Set(categories.map((cat) => cat.id));
  const normalizedQuery = normalizeSearchQuery(searchQuery);

  const groups = subcategories
    .filter((sub) => categoryIds.has(sub.category))
    .map((subcategory) => ({
      designTypes: designTypes.filter(
        (type) =>
          type.subcategoryId === subcategory.id &&
          designTypeMatchesQuery(type, normalizedQuery),
      ),
      subcategory,
    }))
    .filter((group) => group.designTypes.length > 0);

  return (
    <div className="flex flex-col flex-1 min-h-0 max-lg:flex-none">
      <div className="shrink-0 lg:hidden px-xl pt-lg pb-sm">
        <div className="flex h-[36px] items-center gap-sm rounded-lg border border-border-primary bg-bg-primary px-md">
          <SearchIcon className="size-2xl shrink-0 text-fg-quaternary" />
          <input
            className="w-full bg-transparent text-md text-text-primary outline-none placeholder:text-text-placeholder"
            onChange={handleSearchChange}
            placeholder="Search a request type..."
            type="text"
            value={searchQuery}
          />
        </div>
      </div>
      <div className="shrink-0 flex items-center justify-end px-xl lg:px-3xl pt-sm lg:pt-lg pb-sm">
        <SearchButton onSearch={handleSearchChange} value={searchQuery} />
      </div>
      <div className="flex max-lg:flex-none lg:flex-1 lg:min-h-0 flex-col gap-lg max-lg:overflow-y-visible lg:overflow-y-auto overflow-x-hidden pb-2xl pt-xs lg:touch-pan-y lg:overscroll-contain [scrollbar-gutter:stable]">
        {groups.map(({ designTypes: groupDesignTypes, subcategory }) => (
          <DesignTypesCarousel
            designTypes={groupDesignTypes}
            key={subcategory.id}
            subcategoryName={`${subcategory.name} (${CATEGORY_TAG_LABELS[subcategory.category]})`}
          />
        ))}
      </div>
    </div>
  );
}
